import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import api from "@/shared/api";
import { Resource, DataMany, Data, Responsible } from "@/shared/types.ts";
import {
  AdminOrderEntity,
  OrdersEntity,
  OrderStatisticsPeriod,
  OrderStatusStatistic,
} from "@/features/order/types.ts";
import dom from "@/shared/helper/dom.helper.ts";
import { endOfDayISO, startOfDayISO } from "@/shared/helper/datetime.ts";
export * from "./types.ts";

type CreateOrdersRequest = {
  fromCityId: number;
  fromLatitude?: number;
  fromLongitude?: number;
  fromAddress?: string;
  fromApartment?: string;
  fromEntrance?: string;
  fromFloor?: string;

  toCityId: number;
  toLatitude?: number;
  toLongitude?: number;
  toAddress?: string;
  toApartment?: string;
  toEntrance?: string;
  toFloor?: string;

  tariffId: number;

  description?: string;
  category: string;
  isFragile: boolean;
  isDefect: boolean;
  photos: string[];
  contentPhotos: string[];
  comment: string;

  width: number;
  length: number;
  height: number;
  weight: number;
};

// Размер страницы для компактных блоков (заказы в карточке пользователя).
// Потолок limit на сервере — 100.
export const ADMIN_ORDERS_PAGE_SIZE = 10;

// Фильтры списка заказов. Даты — «YYYY-MM-DD», в запрос уходят границами суток
// по Алматы, чтобы выборка не зависела от таймзоны сервера.
export type OrdersFilters = {
  orderId?: number;
  fromCityId?: number;
  toCityId?: number;
  status?: string;
  createdFrom?: string;
  createdTo?: string;
  // Телефоны уходят как есть — сервер сам оставляет от них цифры.
  fromPhone?: string;
  toPhone?: string;
};

export function useOrders({
  enabled = true,
  page = 1,
  limit = 20,
  userId,
  filters,
}: {
  enabled?: boolean;
  page?: number;
  limit?: number;
  // Заказы одного пользователя — любая его роль в заказе.
  userId?: string;
  filters?: OrdersFilters;
}) {
  const searchParams: Record<string, string | number> = { page, limit };

  if (userId) searchParams.userId = userId;
  if (filters?.orderId) searchParams.orderId = filters.orderId;
  if (filters?.fromCityId) searchParams.fromCityId = filters.fromCityId;
  if (filters?.toCityId) searchParams.toCityId = filters.toCityId;
  if (filters?.status) searchParams.status = filters.status;
  if (filters?.fromPhone) searchParams.fromPhone = filters.fromPhone;
  if (filters?.toPhone) searchParams.toPhone = filters.toPhone;
  if (filters?.createdFrom)
    searchParams.createdFrom = startOfDayISO(filters.createdFrom);
  if (filters?.createdTo)
    searchParams.createdTo = endOfDayISO(filters.createdTo);

  return useQuery<DataMany<AdminOrderEntity[]>>({
    queryKey: ["orders", page, limit, userId, filters],
    queryFn: () =>
      api
        .get<DataMany<AdminOrderEntity[]>>(`/api/v1/orders/admin/list/all`, {
          searchParams,
        })
        .json(),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useOrdersStatistics({
  period,
  enabled = true,
}: {
  period: OrderStatisticsPeriod;
  enabled?: boolean;
}) {
  return useQuery<Data<OrderStatusStatistic[]>>({
    queryKey: ["orders", "statistics", period],
    queryFn: () =>
      api
        .get<Data<OrderStatusStatistic[]>>(`/api/v1/orders/admin/statistics`, {
          searchParams: { period },
        })
        .json(),
    enabled,
  });
}

export function useOrdersCreate({ onSuccess }: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation<Resource<OrdersEntity>, Error, CreateOrdersRequest>({
    mutationFn: (data: CreateOrdersRequest) =>
      api.post("/api/v1/orders", { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useOrdersById({
  tariffId,
  enabled = true,
}: {
  tariffId: string | number;
  enabled?: boolean;
}) {
  return useQuery<Resource<OrdersEntity>>({
    queryKey: ["orders", `orders_${tariffId}`],
    queryFn: () =>
      api.get<Resource<OrdersEntity>>(`/api/v1/orders/${tariffId}`).json(),
    enabled,
  });
}

type OrdersUpdateRequest = {
  id: number;
} & CreateOrdersRequest;

export function useOrdersUpdate({ onSuccess }: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation<Resource<OrdersEntity>, Error, OrdersUpdateRequest>({
    mutationFn: (data: OrdersUpdateRequest) =>
      api.put("/api/v1/orders", { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

type OrderDelete = {
  tariffId: number | string;
};

export function useOrdersDelete({ onSuccess }: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation<Resource<OrdersEntity>, Error, OrderDelete>({
    mutationFn: (data) =>
      api.delete(`/api/v1/orders/${data.tariffId}`, { json: data }).json(),
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useAdminOrderById({
  orderId,
  enabled = true,
}: {
  orderId: string | number;
  enabled?: boolean;
}) {
  return useQuery<Data<AdminOrderEntity>>({
    queryKey: ["admin_order", orderId],
    queryFn: () =>
      api
        .get<Data<AdminOrderEntity>>(`/api/v1/orders/admin/by/id/${orderId}`)
        .json(),
    enabled,
  });
}

export function useOrderByBarcode({
  barcode,
  enabled,
}: {
  barcode: string;
  enabled?: boolean;
}) {
  return useQuery<Data<OrdersEntity>>({
    queryKey: [`order_barcode_${barcode}`],
    queryFn: () =>
      api
        .post<
          Data<OrdersEntity>
        >(`/api/v1/orders/operator/by/barcode`, { json: { barcode } })
        .json(),
    enabled,
    retry: 3,
  });
}

export function useOrderInvoiceDocumentByPackageId() {
  return useMutation<Responsible<string>, Error, { packageId: number }>({
    mutationFn: ({ packageId }) =>
      api
        .post<Responsible<string>>(`/api/v1/orders/invoice/exports`, {
          headers: { "content-type": "application/json;" },
          body: JSON.stringify({ packageId }),
        })
        .json(),
    onSuccess: ({ data }) => dom.openFileFromBase64(data),
  });
}

export function useAccountedOrder({ onSuccess }: { onSuccess?: () => void }) {
  return useMutation<Data<OrdersEntity>, void, { barcode: string }>({
    mutationFn: ({ barcode }) => {
      return api
        .post<Data<OrdersEntity>>(
          `/api/v1/orders/admin/order/status/accounted`,
          {
            body: JSON.stringify({ barcode }),
          },
        )
        .json();
    },
    onSuccess: () => {
      onSuccess?.();
    },
  });
}

export function useAccountOrder({ onSuccess }: { onSuccess?: () => void }) {
  return useMutation<Data<{ orderId: number }>, Error, { orderId: number }>({
    mutationFn: ({ orderId }) =>
      api
        .post<
          Data<{ orderId: number }>
        >(`/api/v1/orders/admin/order/status/accounted`, { json: { orderId } })
        .json(),
    onSuccess: () => {
      onSuccess?.();
    },
  });
}

type FocusBody = {
  target: string;
  description: string;
  orderId?: number;
  orderPackageId?: number;
  identification?: string;
};

// Фиксация действия оператора в focus API. Fire-and-forget: не блокирует
// основной поток и глушит ошибки (orderId бэкенд подтянет по identification).
function createFocus(body: FocusBody) {
  void api
    .post(`/api/v1/focus`, { json: body })
    .json()
    .catch(() => {});
}

export function useAccountedPackage({ onSuccess }: { onSuccess?: () => void }) {
  return useMutation<
    Data<{ packageId: number; status: string }>,
    Error,
    { barcode: string }
  >({
    mutationFn: ({ barcode }) =>
      api
        .post<
          Data<{ packageId: number; status: string }>
        >(`/api/v1/orders/admin/package/status/accounted`, { json: { barcode } })
        .json(),
    onSuccess: (data, variables) => {
      createFocus({
        target: "accounted",
        description: "Оператор принимает место на склад",
        orderPackageId: data.data?.packageId,
        identification: variables.barcode,
      });
      onSuccess?.();
    },
  });
}
